import { getLocale } from "../../../features/store-language/i18n.ts";

export type AgencyOtpTimingResponse = {
  serverTime: string;
  expiresAt: string;
  retryAt: string;
};

export type AgencyOtpTiming = {
  codeRequested?: boolean;
  expiresAt: number;
  retryAt: number;
  serverOffset: number;
};

export type AgencyOtpDraft = {
  mobile: string;
  timings: Record<string, AgencyOtpTiming>;
};

const storageKey = "agency-login-otp-timing:v1";
const recipientPattern = /^(?:09\d{9}|[^\s@]+@[^\s@]+\.[^\s@]+)$/;

export function parseAgencyOtpTiming(value: unknown): AgencyOtpTiming | null {
  if (!value || typeof value !== "object") return null;
  const response = value as Record<string, unknown>;
  if (typeof response.serverTime !== "string" || typeof response.expiresAt !== "string"
      || typeof response.retryAt !== "string") return null;
  const serverTime = Date.parse(response.serverTime);
  const expiresAt = Date.parse(response.expiresAt);
  const retryAt = Date.parse(response.retryAt);
  if (![serverTime, expiresAt, retryAt].every(Number.isFinite)) return null;
  return { expiresAt, retryAt, serverOffset: serverTime - Date.now() };
}

export function readAgencyOtpWait(error: unknown): AgencyOtpTiming | null {
  const response = (error as { response?: { data?: unknown } } | null)?.response;
  if (!response?.data || typeof response.data !== "object") return null;
  const details = response.data as Record<string, unknown>;
  return details.code === "agency-otp-wait" ? parseAgencyOtpTiming(details) : null;
}

export function readAgencyOtpDraft(): AgencyOtpDraft {
  const empty: AgencyOtpDraft = { mobile: "", timings: {} };
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || "null");
    if (!saved || typeof saved.mobile !== "string" || !recipientPattern.test(saved.mobile)
        || !saved.timings || typeof saved.timings !== "object") return empty;
    const timings: Record<string, AgencyOtpTiming> = {};
    Object.entries(saved.timings).slice(-10).forEach(function ([mobile, value]) {
      if (!recipientPattern.test(mobile) || !value || typeof value !== "object") return;
      const timing = value as AgencyOtpTiming;
      if (![timing.expiresAt, timing.retryAt, timing.serverOffset].every(Number.isFinite)) return;
      if (Math.max(timing.expiresAt, timing.retryAt) <= Date.now() + timing.serverOffset) return;
      timings[mobile] = timing;
    });
    return { mobile: saved.mobile, timings };
  } catch {
    return empty;
  }
}

export function saveAgencyOtpDraft(draft: AgencyOtpDraft): void {
  try {
    // Only the recipient and server timing are saved. Codes, passwords and selection tokens stay in memory.
    sessionStorage.setItem(storageKey, JSON.stringify(draft));
  } catch {
    // In-memory form state remains usable when browser storage is unavailable.
  }
}

export function clearAgencyOtpDraft(): void {
  try { sessionStorage.removeItem(storageKey); } catch { /* Storage is optional. */ }
}

export function formatAgencyOtpTime(timestamp: number): string {
  return new Intl.DateTimeFormat(getLocale(), {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).format(timestamp);
}

export function formatAgencyOtpCountdown(seconds: number): string {
  const format = new Intl.NumberFormat(getLocale(), { minimumIntegerDigits: 2, useGrouping: false });
  return `${format.format(Math.floor(seconds / 60))}:${format.format(seconds % 60)}`;
}
